import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, X, Check } from 'lucide-react';
import { createSet, addImagesToSet } from "../../api/sets";
import { getTags } from '../../api/hub';
import type { SetSummary } from '../../api/setsTypes';

interface CreateSetModalProps {
  workspaceId: string;
  // Pre-fyll namnet (t.ex. när modalen öppnas från popover med sökterm).
  initialName?: string;
  // Bilder att lägga till direkt vid skapande (för "Create new" från Add-popover).
  initialImageIds?: string[];
  onClose: () => void;
  onCreated: (set: SetSummary) => void;
}

// Standard color-palette — matchar prototypens chips.
const COLOR_PALETTE: { value: string; label: string }[] = [
  { value: '#5fa8d3', label: 'Henrik blue' },
  { value: '#e63946', label: 'Michelle red' },
  { value: '#ffd700', label: 'Gold' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#f0f0f0', label: 'Bone' },
];

// Normaliserar en tag-string så att den passar backend-reglerna.
// Vi gör det defensivt också på klienten: lowercase, mellanslag → bindestreck,
// max 32 char, droppar otillåtna tecken (a-z, 0-9, -, _).
function normalizeTagClient(input: string): string {
  const lower = input.trim().toLowerCase();
  let out = '';
  for (const ch of lower) {
    if (ch === ' ') {
      out += '-';
    } else if (
      (ch >= 'a' && ch <= 'z') ||
      (ch >= '0' && ch <= '9') ||
      ch === '-' ||
      ch === '_'
    ) {
      out += ch;
    }
    if (out.length >= 32) break;
  }
  // Trimma ev. inledande/avslutande bindestreck för snyggare resultat.
  let start = 0;
  while (start < out.length && out[start] === '-') start++;
  let end = out.length;
  while (end > start && out[end - 1] === '-') end--;
  return out.slice(start, end);
}

/**
 * Variant B (Henriks val): "Compact" — namn-fält först, "Advanced…"-toggle
 * expanderar resten (description, color, tags, cover).
 *
 * Återanvänder samma backdrop/modal-pattern som vi gör för other modals
 * (fixed inset-0 + click-outside stänger).
 */
export function CreateSetModal({
  workspaceId,
  initialName = '',
  initialImageIds,
  onClose,
  onCreated,
}: CreateSetModalProps) {
  const queryClient = useQueryClient();
  const nameRef = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>('#5fa8d3');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Hämta tags för auto-complete (suggested + tidigare använda).
  const tagsQuery = useQuery({
    queryKey: ['workspace', workspaceId, 'tags'],
    queryFn: () => getTags(workspaceId),
    staleTime: 60 * 1000,
  });

  // Autofocus namn-fältet på open.
  useEffect(() => {
    nameRef.current?.focus();
    nameRef.current?.select();
  }, []);

  // Esc → stäng
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const set = await createSet({
        workspaceId,
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        tags: tags.length > 0 ? tags : undefined,
      });
      // Om vi fick initialImageIds, lägg till dem direkt.
      if (initialImageIds && initialImageIds.length > 0) {
        await addImagesToSet(set.id, initialImageIds);
      }
      return set;
    },
    onSuccess: (set) => {
      // Refresha hub + sets-listor
      queryClient.invalidateQueries({ queryKey: ['hub'] });
      queryClient.invalidateQueries({ queryKey: ['sets'] });
      onCreated(set);
    },
  });

  function addTag(raw: string) {
    const t = normalizeTagClient(raw);
    if (!t) return;
    if (tags.includes(t)) return;
    if (tags.length >= 24) return; // matchar backend MAX_TAGS_PER_SET
    setTags((prev) => [...prev, t]);
    setTagDraft('');
  }

  function removeTag(t: string) {
    setTags((prev) => prev.filter((x) => x !== t));
  }

  const allSuggested = [
    ...(tagsQuery.data?.suggested ?? []),
    ...(tagsQuery.data?.tags ?? []).map((t) => t.tag),
  ];
  // Dedupe + filtrera bort redan valda
  const suggestionList = Array.from(new Set(allSuggested)).filter(
    (t) => !tags.includes(t),
  );

  const canCreate = name.trim().length > 0 && !createMutation.isPending;

  return (
    <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center px-4 py-12 sm:py-20 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-[460px] bg-loupe-bg-2 border border-loupe-border-2 rounded-2xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Create new set"
      >
        {/* Head */}
        <div className="px-6 py-5 border-b border-loupe-border flex items-center justify-between gap-4">
          <div className="font-display text-xl font-medium tracking-tight">
            New <em className="italic font-normal text-loupe-text-2">set</em>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-transparent border border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text hover:border-loupe-text-3 transition-colors flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Name (prominent display input) */}
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault();
                createMutation.mutate();
              }
            }}
            placeholder="Name your set…"
            maxLength={200}
            className="w-full bg-transparent border-0 border-b border-loupe-border-2 px-0 py-3 font-display text-[28px] font-normal tracking-tight text-loupe-text outline-none transition-colors focus:border-henrik placeholder:text-loupe-text-3 placeholder:italic"
          />

          {/* Advanced toggle */}
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="mt-4 inline-flex items-center gap-2 bg-transparent text-loupe-text-2 hover:text-loupe-text text-xs py-2 transition-colors"
          >
            <ChevronDown
              className={`w-3 h-3 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
            />
            Advanced — description, tags, color
          </button>

          {advancedOpen ? (
            <div className="mt-4 pt-4 border-t border-dashed border-loupe-border">
              {/* Description */}
              <div className="mb-5">
                <label className="block text-[11px] tracking-[0.15em] uppercase text-loupe-text-3 mb-2 font-medium">
                  Description
                  <span className="text-loupe-text-3 font-normal normal-case tracking-normal ml-1.5">
                    — optional
                  </span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  maxLength={2000}
                  placeholder="What's this set about?"
                  className="w-full px-3.5 py-3 bg-loupe-bg-3 border border-loupe-border rounded-[10px] text-loupe-text text-sm outline-none transition-colors resize-none leading-relaxed min-h-[80px] focus:border-henrik focus:bg-[#181818] placeholder:text-loupe-text-3"
                />
              </div>

              {/* Color */}
              <div className="mb-5">
                <label className="block text-[11px] tracking-[0.15em] uppercase text-loupe-text-3 mb-2 font-medium">
                  Color
                </label>
                <div className="flex gap-2.5 items-center flex-wrap">
                  {COLOR_PALETTE.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setColor(c.value)}
                      aria-label={c.label}
                      title={c.label}
                      className={[
                        'w-8 h-8 rounded-full cursor-pointer relative transition-transform border-2',
                        color === c.value
                          ? 'border-white/85 scale-105'
                          : 'border-transparent hover:scale-110',
                      ].join(' ')}
                      style={{ background: c.value }}
                    >
                      {color === c.value ? (
                        <span className="absolute inset-0 flex items-center justify-center text-black/85 text-sm font-bold">
                          ✓
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-[11px] tracking-[0.15em] uppercase text-loupe-text-3 mb-2 font-medium">
                  Tags
                </label>
                <div
                  className="p-2 bg-loupe-bg-3 border border-loupe-border rounded-[10px] flex flex-wrap gap-1.5 items-center min-h-[50px] focus-within:border-henrik transition-colors"
                  onClick={() => tagInputRef.current?.focus()}
                >
                  {tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1 bg-henrik/10 border border-henrik/30 text-henrik rounded-full text-xs font-medium"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTag(t);
                        }}
                        aria-label={`Remove tag ${t}`}
                        className="w-3.5 h-3.5 rounded-full bg-henrik/20 text-henrik flex items-center justify-center text-[10px] hover:bg-henrik/35 transition-colors"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    ref={tagInputRef}
                    type="text"
                    value={tagDraft}
                    onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tagDraft.trim()) {
                        e.preventDefault();
                        addTag(tagDraft);
                      } else if (e.key === 'Backspace' && !tagDraft && tags.length > 0) {
                        // Snabbt ta bort sista chipen
                        setTags((prev) => prev.slice(0, -1));
                      }
                    }}
                    placeholder={tags.length === 0 ? 'Type and press Enter' : ''}
                    className="flex-1 min-w-[120px] bg-transparent border-0 outline-none text-loupe-text text-[13px] px-1.5 py-1 placeholder:text-loupe-text-3"
                  />
                </div>

                {suggestionList.length > 0 ? (
                  <>
                    <div className="text-[10px] tracking-[0.12em] uppercase text-loupe-text-3 mt-3.5 mb-2">
                      Suggested
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {suggestionList.slice(0, 15).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => addTag(s)}
                          className="px-2.5 py-1 bg-transparent border border-dashed border-loupe-border-2 text-loupe-text-3 rounded-full text-[11px] hover:text-henrik hover:border-henrik/35 hover:border-solid transition-all"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Pre-add notice när modalen är öppnad via "Create new" från popover */}
          {initialImageIds && initialImageIds.length > 0 ? (
            <div className="mt-4 px-3 py-2 bg-henrik/10 border border-henrik/30 rounded-lg text-xs text-henrik flex items-center gap-2">
              <Check className="w-3.5 h-3.5" />
              {initialImageIds.length} image{initialImageIds.length === 1 ? '' : 's'} will be added to this set
            </div>
          ) : null}

          {createMutation.isError ? (
            <div className="mt-4 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Could not create set'}
            </div>
          ) : null}
        </div>

        {/* Foot */}
        <div className="px-6 py-4 border-t border-loupe-border bg-black/20 flex items-center justify-between gap-3.5">
          <div className="text-[11px] text-loupe-text-3 tracking-wide hidden sm:block">
            <span className="kbd">Enter</span> to create ·{' '}
            <span className="kbd">Esc</span> to cancel
          </div>
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 bg-transparent border border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text hover:border-loupe-text-3 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors min-h-[40px]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => createMutation.mutate()}
              disabled={!canCreate}
              className={[
                'px-4 py-2.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-all min-h-[40px]',
                canCreate
                  ? 'bg-white text-black hover:scale-[1.03]'
                  : 'bg-white/30 text-black/40 cursor-not-allowed',
              ].join(' ')}
            >
              {createMutation.isPending ? 'Creating…' : 'Create set'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
